import React from "react";
import getImageUrl from "./getImageUrl";

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(text = "", max = 160) {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

export function escapeAttr(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function getOrigin() {
  if (typeof window === "undefined") {
    return process.env.REACT_APP_SERVER_URL?.replace(/\/$/, "") || "https://vlv.am";
  }
  const { origin } = window.location;
  if (origin.includes("localhost") || origin.includes("127.0.0.1")) {
    return process.env.REACT_APP_SERVER_URL?.replace(/\/$/, "") || "https://vlv.am";
  }
  return origin;
}

export function getAbsoluteUrl(path = "") {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;
  const origin = getOrigin();
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export function langPrefix(lang = "hy") {
  return lang && lang !== "hy" ? `/${lang}` : "";
}

export function ogLocale(lang = "hy") {
  if (lang === "ru") return "ru_RU";
  if (lang === "en") return "en_US";
  return "hy_AM";
}

/** Ensure <img> tags in HTML have meaningful alt for Google */
export function enhanceHtmlImageAlts(html = "", fallbackAlt = "") {
  if (!html || !fallbackAlt) return html;
  const alt = escapeAttr(fallbackAlt);
  return html.replace(/<img\b([^>]*)>/gi, (full, attrs = "") => {
    if (/\balt\s*=\s*["'][^"']+["']/i.test(attrs)) return full;
    if (/\balt\s*=\s*["']\s*["']/i.test(attrs)) {
      return `<img${attrs.replace(/\balt\s*=\s*["']\s*["']/i, ` alt="${alt}"`)}>`;
    }
    return `<img alt="${alt}"${attrs}>`;
  });
}

export function JsonLd({ data }) {
  if (!data) return null;
  const items = Array.isArray(data) ? data.filter(Boolean) : [data];
  if (!items.length) return null;
  return (
    <>
      {items.map((item, index) => {
        const payload = JSON.stringify(item);
        // Remount on content change: React often skips updating <script> innerHTML.
        return (
          <script
            key={`${index}-${payload.length}-${payload.slice(0, 48)}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: payload }}
          />
        );
      })}
    </>
  );
}

export function buildProductImageList(product) {
  const images = (product?.gallary_images_api || [])
    .map((img) => getImageUrl(img?.images_source || img?.thum_image_sourc))
    .filter(Boolean);
  if (product?.og_image) {
    return [product.og_image, ...images.filter((url) => url !== product.og_image)];
  }
  return images;
}

export function buildProductJsonLd({
  product,
  productId,
  lang,
  metaTitle,
  metaDescription,
  canonicalUrl,
  breadcrumbs = [],
}) {
  if (!product) return null;

  const name =
    metaTitle ||
    `${product?.brand?.name || ""} ${product?.product_name || ""}`.trim();
  const images = buildProductImageList(product);
  const sku =
    product?.seller_product_id ||
    productId ||
    product?.skus?.[0]?.id ||
    product?.id;
  const mpn = product?.model_number || product?.li_product_id || undefined;
  const priceRaw =
    product?.skus?.[0]?.promo_price ||
    product?.skus?.[0]?.selling_price ||
    product?.online_selling_price ||
    product?.cash_price ||
    product?.average_price;
  const price = priceRaw != null && Number(priceRaw) > 0 ? String(priceRaw) : undefined;
  const category =
    product?.categories?.[0]?.[`name_${lang}`] ||
    breadcrumbs?.[breadcrumbs.length - 1]?.[`name_${lang}`];
  const availability =
    product?.status === 1
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock";

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name,
    description: metaDescription || undefined,
    image: images.length ? images : undefined,
    sku: sku != null ? String(sku) : undefined,
    mpn: mpn != null ? String(mpn) : undefined,
    url: canonicalUrl || undefined,
    brand: product?.brand?.name
      ? { "@type": "Brand", name: product.brand.name }
      : undefined,
    category: category || undefined,
    offers: price
      ? {
          "@type": "Offer",
          url: canonicalUrl || undefined,
          priceCurrency: "AMD",
          price,
          availability,
          itemCondition: "https://schema.org/NewCondition",
        }
      : {
          "@type": "Offer",
          url: canonicalUrl || undefined,
          priceCurrency: "AMD",
          availability,
          itemCondition: "https://schema.org/NewCondition",
        },
  };

  const crumbItems = [
    {
      "@type": "ListItem",
      position: 1,
      name: "VLV",
      item: getAbsoluteUrl(langPrefix(lang) || "/"),
    },
    ...(breadcrumbs || []).map((item, index) => ({
      "@type": "ListItem",
      position: index + 2,
      name: item[`name_${lang}`] || item.name,
      item: getAbsoluteUrl(
        `${langPrefix(lang)}/category/filter/${item.slug}`
      ),
    })),
    {
      "@type": "ListItem",
      position: (breadcrumbs?.length || 0) + 2,
      name,
      item: canonicalUrl,
    },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: crumbItems,
  };

  const imageSchemas = images.slice(0, 8).map((url, index) => ({
    "@context": "https://schema.org/",
    "@type": "ImageObject",
    contentUrl: url,
    url,
    name: `${name}${images.length > 1 ? ` — ${index + 1}` : ""}`,
    description: metaDescription || name,
    caption: name,
  }));

  return [productSchema, breadcrumbSchema, ...imageSchemas];
}

export function buildBlogJsonLd({
  post,
  lang,
  slug,
  metaTitle,
  metaDescription,
  canonicalUrl,
  ogImage,
  schemaType = "BlogPosting",
  listPath = "blog",
  listName = "Blog",
}) {
  if (!post) return null;

  const title = metaTitle || post[`title_${lang}`];
  const image = ogImage || getImageUrl(post[`image_url_${lang}`]);
  const datePublished = post.created_at || undefined;
  const dateModified = post.updated_at || post.created_at || undefined;
  const base = listPath.replace(/^\/|\/$/g, "") || "blog";

  const blogPosting = {
    "@context": "https://schema.org",
    "@type": schemaType,
    headline: title,
    description: metaDescription || undefined,
    image: image ? [image] : undefined,
    datePublished,
    dateModified,
    inLanguage: lang,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    author: {
      "@type": "Organization",
      name: "VLV Centre",
      url: getOrigin(),
    },
    publisher: {
      "@type": "Organization",
      name: "VLV Centre",
      logo: {
        "@type": "ImageObject",
        url: `${getOrigin()}/public/logos/vlv_open_graph.png`,
      },
    },
  };

  const breadcrumb = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "VLV",
        item: getAbsoluteUrl(langPrefix(lang) || "/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: listName,
        item: getAbsoluteUrl(`${langPrefix(lang)}/${base}`),
      },
      {
        "@type": "ListItem",
        position: 3,
        name: title,
        item: canonicalUrl || getAbsoluteUrl(`${langPrefix(lang)}/${base}/${slug}`),
      },
    ],
  };

  const imageObject = image
    ? {
        "@context": "https://schema.org/",
        "@type": "ImageObject",
        contentUrl: image,
        url: image,
        name: title,
        description: metaDescription || title,
        caption: title,
      }
    : null;

  return [blogPosting, breadcrumb, imageObject].filter(Boolean);
}

export function buildProductHreflang(productId) {
  const origin = getOrigin();
  return [
    { hreflang: "hy", href: `${origin}/Product/${productId}` },
    { hreflang: "ru", href: `${origin}/ru/Product/${productId}` },
    { hreflang: "en", href: `${origin}/en/Product/${productId}` },
    { hreflang: "x-default", href: `${origin}/Product/${productId}` },
  ];
}

export function buildBlogHreflang(slug, basePath = "blog") {
  const origin = getOrigin();
  const base = basePath.replace(/^\/|\/$/g, "") || "blog";
  return [
    { hreflang: "hy", href: `${origin}/${base}/${slug}` },
    { hreflang: "ru", href: `${origin}/ru/${base}/${slug}` },
    { hreflang: "en", href: `${origin}/en/${base}/${slug}` },
    { hreflang: "x-default", href: `${origin}/${base}/${slug}` },
  ];
}

export function buildPathHreflang(path = "/", query = "") {
  const origin = getOrigin();
  const [pathnamePart, queryFromPath] = String(path || "/").split("?");
  const normalized =
    pathnamePart === "/"
      ? ""
      : pathnamePart.startsWith("/")
        ? pathnamePart
        : `/${pathnamePart}`;
  const q = query || (queryFromPath ? `?${queryFromPath}` : "");
  const suffix = q.startsWith("?") || !q ? q : `?${q}`;
  return [
    { hreflang: "hy", href: `${origin}${normalized || "/"}${suffix}` },
    { hreflang: "ru", href: `${origin}/ru${normalized || ""}${suffix}` },
    { hreflang: "en", href: `${origin}/en${normalized || ""}${suffix}` },
    { hreflang: "x-default", href: `${origin}${normalized || "/"}${suffix}` },
  ];
}

export function buildBreadcrumbJsonLd(items = [], lang = "hy") {
  const crumbs = [
    {
      "@type": "ListItem",
      position: 1,
      name: "VLV",
      item: getAbsoluteUrl(langPrefix(lang) || "/"),
    },
    ...items
      .filter((item) => item?.name)
      .map((item, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name: item.name,
        item: item.url || undefined,
      })),
  ];

  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: crumbs,
  };
}

export function buildItemListJsonLd(products = [], lang = "hy", listName = "") {
  const elements = (products || [])
    .slice(0, 20)
    .map((raw, index) => {
      const nested = raw?.product && typeof raw.product === "object" ? raw.product : null;
      const product = nested || raw;
      const name =
        product?.[`product_name_${lang}`] ||
        nested?.[`product_name_${lang}`] ||
        product?.product_name ||
        nested?.product_name ||
        product?.name ||
        raw?.name ||
        "";
      // Listing cards often use seller_product / outer id, not nested product.id
      const id =
        raw?.seller_product_id ||
        raw?.seller_id ||
        product?.seller_product_id ||
        product?.seller_id ||
        raw?.id ||
        product?.id ||
        product?.product_id;
      if (!name || !id) return null;
      const image = getImageUrl(
        product?.thum_image_sourc ||
          nested?.thum_image_sourc ||
          product?.gallary_images_api?.[0]?.images_source ||
          product?.image ||
          raw?.image
      );
      return {
        "@type": "ListItem",
        position: index + 1,
        url: getAbsoluteUrl(`${langPrefix(lang)}/Product/${id}`),
        name,
        image: image || undefined,
      };
    })
    .filter(Boolean);

  if (!elements.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName || undefined,
    numberOfItems: elements.length,
    itemListElement: elements,
  };
}

export function buildStaticPageJsonLd({
  title,
  description,
  canonicalUrl,
  lang,
  path,
  schemaType = "WebPage",
  ogImage,
  faqItems,
  breadcrumbs,
  itemList,
  brandName,
}) {
  if (!title) return null;

  const faqMainEntity =
    Array.isArray(faqItems) && faqItems.length
      ? faqItems
          .map((item) => {
            const q = stripHtml(item.question || "");
            const a = stripHtml(item.answer || "");
            if (!q || !a) return null;
            return {
              "@type": "Question",
              name: q,
              acceptedAnswer: {
                "@type": "Answer",
                text: a,
              },
            };
          })
          .filter(Boolean)
      : [];

  // Never emit empty FAQPage — Google rejects FAQ without mainEntity Q/A
  const resolvedType =
    schemaType === "FAQPage" && !faqMainEntity.length ? "WebPage" : schemaType;

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": resolvedType,
    name: title,
    description: description || undefined,
    url: canonicalUrl,
    inLanguage: lang,
    isPartOf: {
      "@type": "WebSite",
      name: "VLV Centre",
      url: getOrigin(),
    },
    primaryImageOfPage: ogImage
      ? { "@type": "ImageObject", url: ogImage, contentUrl: ogImage }
      : undefined,
  };

  if (brandName) {
    pageSchema.about = {
      "@type": "Brand",
      name: brandName,
    };
  }

  if (resolvedType === "FAQPage" && faqMainEntity.length) {
    pageSchema.mainEntity = faqMainEntity;
  }

  const breadcrumb = breadcrumbs?.length
    ? buildBreadcrumbJsonLd(breadcrumbs, lang)
    : buildBreadcrumbJsonLd(
        path && path !== "/"
          ? [{ name: title, url: canonicalUrl }]
          : [],
        lang
      );

  const schemas = [pageSchema, breadcrumb];
  const listSchema = buildItemListJsonLd(itemList, lang, title);
  if (listSchema) schemas.push(listSchema);

  return schemas;
}

/** Title like "Name | VLV Centre" */
export function withBrandSuffix(name, lang = "hy") {
  const clean = stripHtml(name || "").trim();
  if (!clean) {
    return lang === "ru"
      ? "VLV Centre"
      : lang === "en"
        ? "VLV Centre"
        : "VLV Centre";
  }
  if (/VLV/i.test(clean)) return clean;
  return `${clean} | VLV Centre`;
}

export function listingDescription(name, lang = "hy", kind = "category") {
  const n = stripHtml(name || "").trim();
  if (lang === "ru") {
    if (kind === "brand") {
      return n
        ? `Товары бренда ${n} в VLV Centre — каталог, цены и акции.`
        : "Бренды и производители в VLV Centre.";
    }
    if (kind === "search") {
      return n
        ? `Результаты поиска «${n}» в VLV Centre — мебель, техника и электроника.`
        : "Поиск товаров в VLV Centre.";
    }
    if (kind === "sale") {
      return n
        ? `${n} — акция в VLV Centre. Специальные цены и предложения.`
        : "Акции и специальные предложения VLV Centre.";
    }
    return n
      ? `${n} купить в VLV Centre — большой выбор, доставка по Армении.`
      : "Каталог товаров VLV Centre.";
  }
  if (lang === "en") {
    if (kind === "brand") {
      return n
        ? `${n} products at VLV Centre — catalog, prices and deals.`
        : "Brands and manufacturers at VLV Centre.";
    }
    if (kind === "search") {
      return n
        ? `Search results for “${n}” at VLV Centre — furniture, appliances and electronics.`
        : "Search products at VLV Centre.";
    }
    if (kind === "sale") {
      return n
        ? `${n} — promotion at VLV Centre. Special prices and offers.`
        : "Promotions and special offers at VLV Centre.";
    }
    return n
      ? `Buy ${n} at VLV Centre — wide selection and delivery across Armenia.`
      : "VLV Centre product catalog.";
  }
  if (kind === "brand") {
    return n
      ? `${n} բրենդի ապրանքներ VLV Centre-ում՝ կատալոգ, գներ և ակցիաներ։`
      : "Բրենդներ և արտադրողներ VLV Centre-ում։";
  }
  if (kind === "search") {
    return n
      ? `«${n}» որոնման արդյունքներ VLV Centre-ում։`
      : "Ապրանքների որոնում VLV Centre-ում։";
  }
  if (kind === "sale") {
    return n
      ? `${n}՝ ակցիա VLV Centre-ում։ Հատուկ գներ և առաջարկներ։`
      : "Ակցիաներ և հատուկ առաջարկներ VLV Centre-ում։";
  }
  return n
    ? `${n} գնել VLV Centre-ում՝ մեծ ընտրանի և առաքում Հայաստանում։`
    : "VLV Centre ապրանքների կատալոգ։";
}
